import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Search,
  Layout, Grid, Briefcase, BarChart2, ListOrdered,
  MessageSquare, HelpCircle, Mail, Zap, CreditCard,
  Award, PlayCircle, Timer, Video, Package,
  LogOut, Bell, ShieldCheck, Calculator,
  Navigation, PanelBottom, Lock, Type, Sparkles, FileText,
  type LucideIcon,
} from 'lucide-react';
import { LPComponentType, LP_COMPONENT_PALETTE } from './lpComponentTypes';

interface LPComponentBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (type: LPComponentType) => void;
  existingTypes: LPComponentType[];
}

const ICON_MAP: Record<string, LucideIcon> = {
  layout: Layout,
  grid: Grid,
  briefcase: Briefcase,
  'bar-chart-2': BarChart2,
  'list-ordered': ListOrdered,
  'message-square': MessageSquare,
  'help-circle': HelpCircle,
  mail: Mail,
  zap: Zap,
  'credit-card': CreditCard,
  award: Award,
  'play-circle': PlayCircle,
  timer: Timer,
  video: Video,
  package: Package,
  'log-out': LogOut,
  bell: Bell,
  'shield-check': ShieldCheck,
  calculator: Calculator,
  navigation: Navigation,
  'panel-bottom': PanelBottom,
  lock: Lock,
  type: Type,
  sparkles: Sparkles,
  'file-text': FileText,
};

const LPComponentBrowser: React.FC<LPComponentBrowserProps> = ({
  isOpen,
  onClose,
  onAdd,
  existingTypes,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return LP_COMPONENT_PALETTE;
    return LP_COMPONENT_PALETTE.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.type.toLowerCase().includes(q)
    );
  }, [search]);

  if (!isOpen) return null;

  const handleAdd = (type: LPComponentType) => {
    onAdd(type);
    onClose();
    setSearch('');
  };

  const modal = (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 2000,
        }}
      />

      {/* Centred modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '640px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: '80vh',
          zIndex: 2001,
          background: 'var(--panel, rgba(255,255,255,0.97))',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid var(--stroke, rgba(15,23,42,0.10))',
          borderRadius: '14px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px 14px',
            borderBottom: '1px solid var(--stroke, rgba(15,23,42,0.10))',
            flexShrink: 0,
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text)' }}>
            Add Section
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: '6px',
            }}
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--stroke, rgba(15,23,42,0.08))', flexShrink: 0 }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Search sections…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '8px 10px 8px 32px',
                border: '1px solid var(--stroke)', borderRadius: '8px',
                fontSize: '0.875rem', background: 'var(--panel-strong)',
                color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Grid of section cards */}
        <div style={{ overflowY: 'auto', padding: '12px 16px 16px', flex: '1 1 auto' }}>
          {filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '24px' }}>
              No sections match "{search}"
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {filtered.map((item) => {
                const alreadyAdded = existingTypes.includes(item.type);
                const IconComponent = ICON_MAP[item.icon] ?? Layout;
                return (
                  <button
                    key={item.type}
                    onClick={() => handleAdd(item.type)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px',
                      padding: '12px', background: 'var(--panel-strong)',
                      border: '1px solid var(--stroke)', borderRadius: '10px',
                      cursor: 'pointer', textAlign: 'left',
                      opacity: alreadyAdded ? 0.55 : 1,
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!alreadyAdded) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--primary-500,#2177FF)';
                        (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 0 2px var(--primary-100,#D6E8FF)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--stroke)';
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '7px', flexShrink: 0,
                      background: 'var(--primary-50, #EAF3FF)', color: 'var(--primary-500, #2177FF)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <IconComponent size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: '0.8rem', color: 'var(--text)',
                        display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap',
                      }}>
                        {item.label}
                        {item.aiGenerated && (
                          <span style={{
                            fontSize: '0.6rem', fontWeight: 600,
                            background: 'var(--primary-100, #D6E8FF)', color: 'var(--primary-600, #1B60D4)',
                            borderRadius: '9999px', padding: '1px 5px',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                          }}>AI</span>
                        )}
                        {alreadyAdded && (
                          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                            added
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '2px', lineHeight: 1.35 }}>
                        {item.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default LPComponentBrowser;
